import { useEffect, useRef, useState } from "react";
import Select from "react-select";
import DatePicker from "react-multi-date-picker";
import { TagCloseIconBlack } from "../../svg";
import IconBtn from "../../form/icon-btn";

interface Option {
  id: number;
  value: string;
  label: string;
}

interface SelectDurationProps {
  options: Option[];
  labelName: string;
  getOptionLabel: (option: Option) => string;
  getOptionValue: (option: Option) => string;
  isReset: boolean;
  isDisabled: boolean;
  handleChange: (option: Option | null) => void;
  defaultValue: Option;
  setStartDate?: any;
  setEndDate?: any;
  onClose?: any;
}

function SelectDuration({
  options,
  labelName,
  getOptionLabel,
  getOptionValue,
  isReset,
  isDisabled,
  handleChange,
  defaultValue,
  setStartDate,
  setEndDate,
  onClose,
}: SelectDurationProps) {
  const selectRef = useRef<any>(null);
  const [selectedOption, setSelectedOption] = useState<Option | null>(null);
  const [dateRange, setDateRange] = useState<any>([null, null]);
  const datePickerRef = useRef<any>(null);

  useEffect(() => {
    if (isReset) {
      selectRef?.current?.clearValue();
      setSelectedOption(null);
      setDateRange([null, null]);
    }
  }, [isReset]);

  const handleSelectChange = (option: Option | null) => {
    setSelectedOption(option);
    if (option?.value !== "custom") {
      setDateRange([null, null]);
    }
    handleChange(option);
  };

  useEffect(() => {
    if (selectedOption?.value === "custom" && datePickerRef.current) {
      datePickerRef.current?.openCalendar();
    }
  }, [selectedOption]);

  useEffect(() => {
    if (dateRange && dateRange.length == 2) {
      setTimeout(() => {
        if (datePickerRef.current) {
          datePickerRef.current.closeCalendar();
        }
      }, 1000);
    }
  }, [dateRange]);

  const handleInputChange = (newRange: any) => {
    if (newRange && newRange.length == 2) {
      setTimeout(() => {
        if (datePickerRef.current) {
          datePickerRef.current.closeCalendar();
        }
      }, 1000);
      setStartDate(newRange[0].value);
      setEndDate(newRange[1].value);
    }
  };

  const handleDateChange = (newRange: any) => {
    const formattedRange = newRange.map((timestamp: any, index: number) => {
      const date = new Date(timestamp);
      const year = date.getUTCFullYear();
      const month = String(date.getUTCMonth() + 1).padStart(2, "0");
      const day = String(date.getUTCDate()).padStart(2, "0");
      const formattedDate = `${year}-${month}-${day}`;

      return {
        id: index + 1,
        value: formattedDate,
      };
    });

    handleInputChange(formattedRange);
  };

  return (
    <div className="w-full h-auto">
      {selectedOption?.value !== "custom" && (
        <Select
          options={options}
          placeholder={labelName}
          classNamePrefix="cus-select"
          getOptionLabel={getOptionLabel}
          getOptionValue={getOptionValue}
          value={selectedOption || defaultValue}
          menuPosition={"fixed"}
          ref={selectRef}
          isDisabled={isDisabled}
          isSearchable={false}
          onChange={handleSelectChange}
        />
      )}

      {selectedOption?.value === "custom" && (
        <div className="w-full h-auto">
          <div className="w-full h-auto flex justify-between items-center">
            <div className="relative w-[98%]">
              <DatePicker
                ref={datePickerRef}
                value={dateRange}
                onChange={(newRange: any) => handleDateChange(newRange)}
                range
                rangeHover
                dateSeparator=" to "
                portal
                inputClass="cus-date-range text-[1.6rem] w-full pr-[2rem]" // Added padding-right for "X" visibility
                format="YYYY-MM-DD"
              />
              {/* The "X" icon positioned at the right of the input */}
              <div className="absolute  right-2 top-1/2 transform -translate-y-1/2 cursor-pointer pr-4">
                <IconBtn
                  icon={<TagCloseIconBlack />}
                  size="w-[1.5rem]"
                  handleClick={() => {
                    setSelectedOption(null);
                    setDateRange([null, null]); // Clear date range
                    if (onClose) onClose(); // Call the onClose function
                    selectRef.current?.focus(); // Reopen dropdown after clearing custom selection
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SelectDuration;